import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const LearningHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  const [progress, setProgress] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [newIdea, setNewIdea] = useState({ title: '', description: '' });

  useEffect(() => {
    if (!student) {
      navigate('/student-login');
      return;
    }
    loadLearningData();
  }, [student, navigate]);

  const loadLearningData = async () => {
    try {
      const [progressRes, ideasRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/progress/${student.username}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/ideas`)
      ]);

      const progressData = await progressRes.json();
      const ideasData = await ideasRes.json();

      setProgress(progressData);
      setIdeas(ideasData);
    } catch (error) {
      console.error('Error loading learning data:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitIdea = async (e) => {
    e.preventDefault();
    if (!newIdea.title.trim() || !newIdea.description.trim()) {
      alert('Please fill in both title and description.');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/ideas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: student.username,
          title: newIdea.title.trim(),
          description: newIdea.description.trim()
        })
      });

      if (response.ok) {
        setNewIdea({ title: '', description: '' });
        loadLearningData(); // Refresh ideas
        alert('Idea submitted successfully!');
      } else {
        alert('Failed to submit idea. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting idea:', error);
      alert('Failed to submit idea. Please try again.');
    }
  };

  const voteOnIdea = async (ideaId, voteType) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/ideas/${ideaId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: student.username,
          voteType: voteType
        })
      });

      if (response.ok) {
        loadLearningData(); // Refresh ideas with updated votes
      }
    } catch (error) {
      console.error('Error voting on idea:', error);
    }
  };

  const getSubjectProgress = () => {
    const subjectStats = {};

    progress.forEach(item => {
      if (!subjectStats[item.subject]) {
        subjectStats[item.subject] = {
          subject: item.subject,
          totalLessons: 0,
          completedLessons: 0,
          averageScore: 0,
          lastUpdated: item.lastUpdated
        };
      }

      subjectStats[item.subject].totalLessons++;
      if (item.completed) {
        subjectStats[item.subject].completedLessons++;
      }
      subjectStats[item.subject].averageScore += item.score || 0;
    });

    // Calculate averages
    Object.values(subjectStats).forEach(stats => {
      stats.averageScore = stats.totalLessons > 0
        ? Math.round((stats.averageScore / stats.totalLessons) * 100) / 100
        : 0;
      stats.completionRate = stats.totalLessons > 0
        ? Math.round((stats.completedLessons / stats.totalLessons) * 100)
        : 0;
    });

    return Object.values(subjectStats);
  };

  const getOverallStats = () => {
    const totalLessons = progress.length;
    const completedLessons = progress.filter(p => p.completed).length;
    const averageScore = progress.length > 0
      ? progress.reduce((sum, p) => sum + (p.score || 0), 0) / progress.length
      : 0;

    return {
      totalLessons,
      completedLessons,
      averageScore: Math.round(averageScore * 100) / 100,
      completionRate: totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0
    };
  };

  const getFilteredIdeas = () => {
    if (selectedSubject === 'all') return ideas;
    return ideas.filter(idea => idea.category === selectedSubject);
  };

  if (!student) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading learning hub...</div>
      </div>
    );
  }

  const subjectProgress = getSubjectProgress();
  const overallStats = getOverallStats();
  const filteredIdeas = getFilteredIdeas();

  return (
    <div className="min-h-screen bg-blue-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">Learning Hub</h1>
          <button
            onClick={() => navigate('/student-dashboard', { state: { student } })}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Overall Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Books</div>
            <h3 className="text-lg font-semibold text-gray-800">{overallStats.totalLessons}</h3>
            <p className="text-sm text-gray-600">Total Lessons</p>
          </div>

          <div className="bg-green-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">OK</div>
            <h3 className="text-lg font-semibold text-green-800">{overallStats.completedLessons}</h3>
            <p className="text-sm text-green-600">Completed</p>
          </div>

          <div className="bg-blue-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Stats</div>
            <h3 className="text-lg font-semibold text-blue-800">{overallStats.completionRate}%</h3>
            <p className="text-sm text-blue-600">Completion Rate</p>
          </div>

          <div className="bg-yellow-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Goals</div>
            <h3 className="text-lg font-semibold text-yellow-800">{overallStats.averageScore}</h3>
            <p className="text-sm text-yellow-600">Avg Score</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Subject Progress */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Subject Progress</h2>
            {subjectProgress.length === 0 ? (
              <p className="text-gray-600">No progress data available yet.</p>
            ) : (
              <div className="space-y-4">
                {subjectProgress.map((subject) => (
                  <div key={subject.subject} className="border rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-gray-800">{subject.subject}</h3>
                      <span className="text-sm text-gray-500">
                        {subject.completedLessons}/{subject.totalLessons} completed
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                      <div
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${subject.completionRate}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{subject.completionRate}% Complete</span>
                      <span>Avg Score: {subject.averageScore}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Learning Recommendations */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Learning Recommendations</h2>
            <div className="space-y-4">
              {subjectProgress
                .filter(subject => subject.completionRate < 80)
                .sort((a, b) => a.completionRate - b.completionRate)
                .slice(0, 3)
                .map((subject) => (
                  <div key={subject.subject} className="border-l-4 border-red-500 pl-4 py-2">
                    <h3 className="font-semibold text-gray-800">{subject.subject}</h3>
                    <p className="text-sm text-gray-600">
                      Focus on completing remaining lessons. Current progress: {subject.completionRate}%
                    </p>
                  </div>
                ))}

              {subjectProgress.every(subject => subject.completionRate >= 80) && (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">Success</div>
                  <h3 className="text-lg font-semibold text-green-800 mb-2">Excellent Progress!</h3>
                  <p className="text-gray-600">You're doing great across all subjects. Keep it up!</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ideas & Suggestions */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Ideas & Suggestions</h2>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="all">All Ideas</option>
              <option value="curriculum">Curriculum</option>
              <option value="technology">Technology</option>
              <option value="facilities">Facilities</option>
              <option value="activities">Activities</option>
            </select>
          </div>

          {/* Submit New Idea */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Share Your Ideas</h3>
            <form onSubmit={submitIdea} className="space-y-3">
              <input
                type="text"
                placeholder="Idea title..."
                value={newIdea.title}
                onChange={(e) => setNewIdea({...newIdea, title: e.target.value})}
                className="w-full border rounded px-3 py-2"
                required
              />
              <textarea
                placeholder="Describe your idea..."
                value={newIdea.description}
                onChange={(e) => setNewIdea({...newIdea, description: e.target.value})}
                className="w-full border rounded px-3 py-2"
                rows="3"
                required
              />
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Submit Idea
              </button>
            </form>
          </div>

          {/* Ideas List */}
          <div className="space-y-4">
            {filteredIdeas.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No ideas submitted yet. Be the first to share!</p>
            ) : (
              filteredIdeas.map((idea) => (
                <div key={idea.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800">{idea.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">
                        By {idea.username} ? {new Date(idea.date).toLocaleDateString()}
                      </p>
                      <p className="text-gray-700">{idea.description}</p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => voteOnIdea(idea.id, 'up')}
                        className="text-green-600 hover:text-green-800"
                      >
                        Like
                      </button>
                      <span className="font-semibold">{idea.votes || 0}</span>
                      <button
                        onClick={() => voteOnIdea(idea.id, 'down')}
                        className="text-red-600 hover:text-red-800"
                      >
                        Dislike
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningHub;





